import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { axiosUsers, axiosDoctorRegister } from "../api/axios";
import { homeByRole } from "../utils/homeByRole";
import Cookies from "js-cookie";
import { getSessionLogoutRole } from "../auth/sessionLogout";

/**
 * PublicRouteAuth
 * - Appelé autour des pages publiques (landing, login...).
 * - Il n'existe pas de endpoint /auth/me unique côté backend : le patient
 *   est vérifié via GET /users/me (cookie httpOnly, withCredentials) et le
 *   médecin via GET /register-doctors/me (token lisible côté client).
 * - Pendant le chargement on n'affiche rien (évite le flash de la page publique).
 * - Si une session valide existe -> redirection selon rôle.
 * - Si 401/403 (ou aucune session) -> rend les enfants (page publique).
 */
export default function PublicRouteAuth({ children }) {
  const [status, setStatus] = useState(() =>
    getSessionLogoutRole() ? "unauth" : "loading"
  ); // loading | authed | unauth

  const [user, setUser] = useState(null);

  useEffect(() => {
    if (getSessionLogoutRole()) return undefined;

    let cancelled = false;

    const getDoctorToken = () =>
      Cookies.get("doctorAccessToken") ||
      Cookies.get("doctor_token") ||
      Cookies.get("doctorToken") ||
      localStorage.getItem("doctorAccessToken") ||
      localStorage.getItem("doctor_token") ||
      localStorage.getItem("doctorToken") ||
      null;

    async function probeDoctor() {
      const token = getDoctorToken();
      if (!token) return null;

      const res = await axiosDoctorRegister.get("/me", {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload = res?.data?.data ?? res?.data ?? null;
      if (!payload) return null;

      const id = payload?.id || payload?._id || payload?.doctorId || payload?.userId || null;
      return { role: "doctor", id, raw: payload };
    }

    async function probePatient() {
      // Le cookie accessToken patient est httpOnly : withCredentials suffit,
      // le JS n'a pas besoin (et ne peut pas) le lire explicitement.
      const res = await axiosUsers.get("/me", { withCredentials: true });
      const payload = res?.data?.data ?? res?.data ?? null;
      if (!payload) return null;

      const id = payload?.id || payload?._id || payload?.userId || Cookies.get("userId") || null;
      return { role: "patient", id, raw: payload };
    }

    async function probe() {
      setStatus("loading");

      // 1) Tenter la session médecin (token lisible, évite un appel inutile sinon)
      try {
        const doctor = await probeDoctor();
        if (cancelled) return;
        if (doctor) {
          setUser(doctor);
          setStatus("authed");
          return;
        }
      } catch (err) {
        // 401/403 => pas de session médecin valide, on continue sur le patient
        if (cancelled) return;
      }

      // 2) Tenter la session patient (cookie httpOnly)
      try {
        const patient = await probePatient();
        if (cancelled) return;
        if (patient) {
          setUser(patient);
          setStatus("authed");
          return;
        }
        setStatus("unauth");
      } catch (err) {
        if (cancelled) return;
        const statusCode = err?.response?.status;
        if (statusCode !== 401 && statusCode !== 403) {
          // Erreur réseau/serveur : on reste prudent et on affiche la page publique
          console.error("[PublicRouteAuth] probe error:", err);
        }
        setStatus("unauth");
      }
    }

    probe();

    return () => {
      cancelled = true;
    };
  }, []);

  // Pendant le chargement, on n'affiche rien (évite le flash de la landing/login)
  if (status === "loading") return null;

  // Si authentifié, rediriger vers le bon feed
  if (status === "authed" && user) {
    const path = homeByRole(user.role, user.id);
    return <Navigate to={path} replace />;
  }

  // Sinon (unauth) : afficher la page publique normalement
  return children;
}
